import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import SEO from '@/components/SEO';
import { BookOpen, ShoppingBag, Award, Users, Calendar, TrendingUp, Star, CheckCircle2 } from 'lucide-react';

const LandingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      title: "Sun Kamal Academy - Bringing Light Through Knowledge",
      subtitle: "Learn Chess, Tabla, Yoga and more from expert instructors online",
      cta1: "Explore Courses",
      cta2: "Shop Equipment",
      image: "https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=800"
    },
    {
      title: "Chess for Beginners & Intermediates",
      subtitle: "8-week comprehensive course with Suraj A. Kumar Bidhani",
      cta1: "Learn Chess",
      cta2: "View Schedule",
      image: "https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=800"
    },
    {
      title: "Master the Tabla & Drums",
      subtitle: "Professional drumming instruction from basics to advanced",
      cta1: "Start Learning",
      cta2: "View Details",
      image: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=800"
    }
  ];

  const features = [
    {
      icon: <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500" />,
      title: "Online Live Classes",
      description: "Interactive Zoom sessions with expert instructors. All classes are recorded for your review and practice."
    },
    {
      icon: <Award className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500" />,
      title: "Certificate on Completion",
      description: "Earn certificates for completed courses. Showcase your achievements and newly acquired skills."
    },
    {
      icon: <Users className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500" />,
      title: "Small Batch Sizes",
      description: "Maximum 8-10 students per class ensures personalized attention and effective learning."
    },
    {
      icon: <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500" />,
      title: "Flexible Payment Plans",
      description: "Pay in installments (50% upfront, 50% midway). Early bird and sibling discounts available!"
    }
  ];

  const categories = [
    { name: "Chess", icon: "♟", classes: 1 },
    { name: "Tabla", icon: "🥁", classes: 1 },
    { name: "Yoga", icon: "🧘", classes: 1 },
    { name: "Drums", icon: "🪘", classes: 1 },
    { name: "Checkers", icon: "🎲", classes: 0 },
    { name: "Art & Craft", icon: "🎨", classes: 0 }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getDashboardRoute = () => {
    if (!user) return '/auth';
    return `/dashboard/${user.role}`;
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Home"
        description="Sun Kamal Academy - Culture & Arts Academy. Learn Chess, Tabla, Yoga & more from expert instructors. Online live classes with certificates."
        path="/"
      />
      <Header />

      {/* Hero Section */}
      <section className="hero-section pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-24 relative overflow-hidden" data-testid="hero-section" style={{
        backgroundImage: `linear-gradient(rgba(255, 245, 240, 0.9), rgba(255, 249, 230, 0.9)), url(${heroSlides[currentSlide].image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.8s ease-in-out'
      }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-6 text-gray-900 animate-fade-in" data-testid="hero-title" style={{
              animation: 'fadeInUp 1s ease-out'
            }}>
              {heroSlides[currentSlide].title}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto animate-fade-in px-2" data-testid="hero-subtitle" style={{
              animation: 'fadeInUp 1s ease-out 0.2s both'
            }}>
              {heroSlides[currentSlide].subtitle}
            </p>
            <div className="flex gap-3 sm:gap-4 justify-center flex-wrap px-4" style={{
              animation: 'fadeInUp 1s ease-out 0.4s both'
            }}>
              <button className="btn-primary transform hover:scale-105 transition-transform duration-300" onClick={() => navigate('/classes')} data-testid="hero-cta-1">
                {heroSlides[currentSlide].cta1}
              </button>
              <button className="btn-secondary transform hover:scale-105 transition-transform duration-300" onClick={() => navigate('/shop')} data-testid="hero-cta-2">
                {heroSlides[currentSlide].cta2}
              </button>
            </div>
            <div className="flex gap-2 justify-center mt-6 sm:mt-8">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index ? 'bg-orange-500 w-8' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  data-testid={`slide-indicator-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4">Unlock Your Potential</h2>
          <p className="text-center text-gray-600 mb-8 sm:mb-12 text-base sm:text-lg">Key features of our platform</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-testid={`feature-card-${index}`}>
                <div className="mb-3 sm:mb-4 flex justify-center">{feature.icon}</div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50" id="courses" data-testid="courses-section">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4">Our Courses & Categories</h2>
          <p className="text-center text-gray-600 mb-8 sm:mb-12 text-base sm:text-lg">Explore what we offer</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6">
            {categories.map((cat, index) => (
              <div
                key={index}
                className="card text-center cursor-pointer"
                onClick={() => navigate(`/classes?sport=${cat.name}`)}
                data-testid={`category-card-${index}`}
              >
                <div className="sport-icon" aria-hidden="true">{cat.icon}</div>
                <h3 className="font-bold text-base sm:text-lg mb-1">{cat.name}</h3>
                <p className="text-gray-500 text-xs sm:text-sm">{cat.classes} {cat.classes === 1 ? 'course' : 'courses'}</p>
                <Button variant="ghost" className="mt-2 text-orange-500 text-sm" data-testid={`category-explore-${index}`}>
                  Explore →
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-12 sm:py-16 md:py-20 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-center">
            <div className="p-6 sm:p-8">
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4" aria-hidden="true">🎯</div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Mission</h3>
              <p className="text-gray-600 text-sm sm:text-base">To bring light of knowledge and music through teaching, empowering students of all ages to discover their potential.</p>
            </div>
            <div className="p-6 sm:p-8">
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4" aria-hidden="true">🌸</div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Vision</h3>
              <p className="text-gray-600 text-sm sm:text-base">Be like Kamal (lotus) and grow into beautiful creations - rising above challenges with grace and beauty.</p>
            </div>
            <div className="p-6 sm:p-8">
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4" aria-hidden="true">💎</div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Values</h3>
              <p className="text-gray-600 text-sm sm:text-base">Integrity, Love, and Hard Work - the foundation of everything we teach and practice at Sun Kamal Academy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-orange-500 to-yellow-500" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-white text-center">
            <div data-testid="stat-students">
              <Users className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3" />
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Growing</div>
              <div className="text-sm sm:text-lg">Happy Students</div>
            </div>
            <div data-testid="stat-instructors">
              <Award className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3" />
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">100%</div>
              <div className="text-sm sm:text-lg">Dedication</div>
            </div>
            <div data-testid="stat-classes">
              <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3" />
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">8 Weeks</div>
              <div className="text-sm sm:text-lg">Structured Courses</div>
            </div>
            <div data-testid="stat-success">
              <CheckCircle2 className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3" />
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Est. 2024</div>
              <div className="text-sm sm:text-lg">Founded by Suraj Bidhani</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 md:py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12">What Students Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div className="card" data-testid="testimonial-1">
              <div className="flex gap-1 mb-3 sm:mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">
                "The Chess course was fantastic! Suraj's teaching style is clear and engaging. I went from barely knowing the rules to confidently playing tournaments."
              </p>
              <div className="font-semibold text-sm sm:text-base">Sarah M.</div>
              <div className="text-xs sm:text-sm text-gray-500">Chess Student</div>
            </div>
            <div className="card" data-testid="testimonial-2">
              <div className="flex gap-1 mb-3 sm:mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">
                "Best online learning experience! The small class size meant I got personal attention. The installment payment option made it affordable too."
              </p>
              <div className="font-semibold text-sm sm:text-base">Michael R.</div>
              <div className="text-xs sm:text-sm text-gray-500">Tabla Student</div>
            </div>
            <div className="card" data-testid="testimonial-3">
              <div className="flex gap-1 mb-3 sm:mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">
                "The recorded sessions were a lifesaver! I could review lessons anytime. Got my certificate and feel so accomplished!"
              </p>
              <div className="font-semibold text-sm sm:text-base">Priya K.</div>
              <div className="text-xs sm:text-sm text-gray-500">Chess Student</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-blue-900 to-blue-700 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">Ready to Start Your Journey?</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90">
            Join Sun Kamal Academy and discover your potential through culture and arts
          </p>
          <div className="flex gap-3 sm:gap-4 justify-center flex-wrap">
            <button className="bg-white text-blue-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:bg-gray-100 transition" onClick={() => navigate('/auth')} data-testid="cta-signup">
              Sign Up Now
            </button>
            <button className="border-2 border-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:bg-white hover:text-blue-900 transition" onClick={() => navigate('/classes')} data-testid="cta-browse">
              Browse Classes
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="https://customer-assets.emergentagent.com/job_sunkamalplay/artifacts/oha9raa2_logo-kamal.webp" alt="Sun Kamal Academy" className="h-12 sm:h-16" />
              </div>
              <p className="text-gray-400 mb-4 text-sm sm:text-base">
                Sun Kamal Academy - Bringing light of knowledge through teaching
              </p>
              <p className="text-gray-400 text-xs sm:text-sm">
                Founded 2024 by Suraj A. Kumar Bidhani
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-3 sm:mb-4">Courses</h3>
              <div className="space-y-2">
                <Link to="/classes?sport=Chess" className="block text-gray-400 hover:text-white transition text-sm">Chess</Link>
                <Link to="/classes?sport=Tabla" className="block text-gray-400 hover:text-white transition text-sm">Tabla</Link>
                <Link to="/classes?sport=Yoga" className="block text-gray-400 hover:text-white transition text-sm">Yoga</Link>
                <Link to="/shop" className="block text-gray-400 hover:text-white transition text-sm">Shop Equipment</Link>
              </div>
            </div>
            <div>
              <h3 className="font-bold mb-3 sm:mb-4">Academy Info</h3>
              <div className="space-y-2">
                <Link to="/auth" className="block text-gray-400 hover:text-white transition text-sm">Student Portal</Link>
                <Link to="/auth" className="block text-gray-400 hover:text-white transition text-sm">Teacher Portal</Link>
                <a href="#courses" className="block text-gray-400 hover:text-white transition text-sm">Our Courses</a>
              </div>
            </div>
            <div>
              <h3 className="font-bold mb-3 sm:mb-4">Contact</h3>
              <div className="space-y-2">
                <a href="mailto:info@sunkamalsports.online" className="block text-gray-400 hover:text-white transition text-sm">info@sunkamalsports.online</a>
                <p className="text-gray-400 text-xs sm:text-sm mt-4">
                  <strong>Values:</strong> Integrity, Love, Hard Work
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-gray-400">
            <p className="text-sm">© 2024 Sun Kamal Academy. All rights reserved.</p>
            <p className="text-xs sm:text-sm mt-2">Be like Kamal (lotus) and grow into beautiful creations</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
